import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import ReCAPTCHA from 'react-google-recaptcha';
import '../assets/styles/GetStartedPage.css';

const steps = [
  {
    num: '01',
    title: 'We review your submission',
    desc: 'Within 12 hours we go through your answers and get familiar with your business.',
  },
  {
    num: '02',
    title: 'Schedule a free consultation',
    desc: "We'll reach out to book a short call to align on goals, timeline, and fit.",
  },
  {
    num: '03',
    title: 'Receive your custom proposal',
    desc: 'You get a tailored plan and pricing — no generic quotes, no surprises.',
  },
];

const stats = [
  { number: '100%', label: 'Customer satisfaction' },
  { number: '4 wks', label: 'Avg. delivery time' },
  { number: 'Free', label: 'Consultation included' },
];

const GetStartedPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    business: '',
    service: '',
    budget: '',
    stage: '',
    problems: '',
    success: '',
    company: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef();

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.company && formData.company.trim() !== '') return;

    setLoading(true);
    setMessage('');

    try {
      const token = await recaptchaRef.current.executeAsync();

      if (!token) {
        setMessage("Please verify you're human.");
        setLoading(false);
        return;
      }

      const botPhrases = [
        'please contact me',
        'please reach out',
        'send some information',
        'i found your ad',
        'waiting for your contact',
      ];

      const isLikelyBot = botPhrases.some((phrase) =>
        [formData.business, formData.problems, formData.success]
          .some((field) => field.toLowerCase().includes(phrase))
      );

      if (isLikelyBot) {
        setMessage('Please provide more specific details about your project.');
        setLoading(false);
        return;
      }

      if (
        formData.business.length < 15 ||
        formData.problems.length < 15 ||
        formData.success.length < 15
      ) {
        setMessage('Please provide more detail in your answers.');
        setLoading(false);
        return;
      }

      const allAnswers = [formData.business, formData.problems, formData.success];
      const areAnswersTooSimilar = allAnswers.every(
        (val, i, arr) => val.trim() === arr[0].trim()
      );

      if (areAnswersTooSimilar) {
        setMessage('Please avoid repeating the same answer in every field.');
        setLoading(false);
        return;
      }

      await addDoc(collection(db, 'quotes'), {
        ...formData,
        recaptchaToken: token,
        timestamp: new Date(),
      });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-11558218396/7xyTCPqvuMkaEJzVsYcr',
        });
      }

      window.location.href = '/thank-you';

      setFormData({
        name: '', email: '', phone: '', website: '',
        business: '', service: '', budget: '', stage: '',
        problems: '', success: '', company: '',
      });
    } catch (error) {
      setMessage(`Failed to send request: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="get-started-page">

      {/* Hero — editorial warm style */}
      <section className="gs-hero">
        <div className="gs-hero-inner">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Get started
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Tell us about your project.<br />
            <span>We'll take it from there.</span>
          </motion.h1>
          <motion.p
            className="gs-hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Fill out the form below and we'll be in touch within 24 hours to discuss your goals.
          </motion.p>
        </div>
      </section>

      {/* Form section */}
      <div className="form-section-container">

        {/* Left panel */}
        <div className="form-intro">

          {/* Trust stats */}
          <div className="gs-stats">
            {stats.map((s) => (
              <div className="gs-stat" key={s.label}>
                <span className="gs-stat-number">{s.number}</span>
                <span className="gs-stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="gs-divider" />

          {/* What happens next */}
          <div className="gs-next">
            <p className="eyebrow">What happens next</p>
            <div className="gs-steps">
              {steps.map((step) => (
                <div className="gs-step" key={step.num}>
                  <span className="gs-step-num">{step.num}</span>
                  <div>
                    <p className="gs-step-title">{step.title}</p>
                    <p className="gs-step-desc">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Form */}
        <div className="get-started-form">
          <form onSubmit={handleSubmit}>

            <div>
              <label htmlFor="name">Name</label>
              <input type="text" id="name" value={formData.name} onChange={handleChange} placeholder="e.g., John Doe" required />
            </div>

            <div>
              <label htmlFor="email">Email</label>
              <input type="email" id="email" value={formData.email} onChange={handleChange} placeholder="e.g., john@example.com" required />
            </div>

            <div>
              <label htmlFor="phone">Phone number (optional)</label>
              <input type="tel" id="phone" value={formData.phone} onChange={handleChange} placeholder="e.g., (123) 456-7890" />
            </div>

            <div>
              <label htmlFor="website">Website and social platforms (optional)</label>
              <input type="text" id="website" value={formData.website} onChange={handleChange} placeholder="e.g., www.example.com, @example" />
            </div>

            <div>
              <label htmlFor="business">What does your business do and how did you get there?</label>
              <textarea id="business" value={formData.business} onChange={handleChange} placeholder="e.g., We sell eco-friendly home decor items." required />
            </div>

            <div>
              <label htmlFor="service">Which service are you interested in?</label>
              <select id="service" value={formData.service} onChange={handleChange} required>
                <option value="" disabled>Select a service</option>
                <option value="Starter Website">Starter Website - $997</option>
                <option value="Growth Website">Growth Website - $1,997</option>
                <option value="Authority Website">Authority Website - $3,500</option>
                <option value="Care Plan">Monthly Care Plan</option>
                <option value="Custom Website or Web App">Custom Website / Web App</option>
              </select>
            </div>

            <div>
              <label htmlFor="budget">What's your budget for this project?</label>
              <select id="budget" value={formData.budget} onChange={handleChange} required>
                <option value="" disabled>Select your budget range</option>
                <option value="Under $1,000">Under $1,000</option>
                <option value="$1,000 - $2,000">$1,000 - $2,000</option>
                <option value="$2,000 - $3,500">$2,000 - $3,500</option>
                <option value="$3,500+">$3,500+</option>
              </select>
            </div>

            <div>
              <label htmlFor="stage">What stage are you at in your business or project?</label>
              <select id="stage" value={formData.stage || ''} onChange={handleChange} required>
                <option value="" disabled>Select a stage</option>
                <option value="Just an idea">Just an idea</option>
                <option value="Planning and research">Planning and research</option>
                <option value="Actively running a business">Actively running a business</option>
                <option value="Rebranding or upgrading">Rebranding or upgrading</option>
              </select>
            </div>

            <div>
              <label htmlFor="problems">What problems are you hoping to solve?</label>
              <textarea id="problems" value={formData.problems} onChange={handleChange} placeholder="e.g., Increase online sales and improve website speed." minLength={10} />
            </div>

            <div>
              <label htmlFor="success">What does success look like for this project?</label>
              <textarea id="success" value={formData.success} onChange={handleChange} placeholder="e.g., A user-friendly site with higher traffic and sales." minLength={10} />
            </div>

            {/* Honeypot */}
            <input
              type="text"
              id="company"
              style={{ display: 'none' }}
              value={formData.company || ''}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />

            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Ld5Z1ErAAAAANR5ClNn0R1nJc_8JMFu_00mlNnS"
              size="invisible"
              badge="bottomright"
            />

            <button type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit request'}
            </button>
          </form>
          {message && <p className="message">{message}</p>}
        </div>

      </div>
    </div>
  );
};

export default GetStartedPage;
